import * as zod from 'zod';

/**
 * Shared contract for the echo-bot configuration panel.
 *
 * `config` is **snake_case**, 1:1 with `echo-bot-config.yaml` under the
 * Dynaconf `default:` envelope — no camelCase remap, so dump/load round-trips
 * cleanly. Keys are classified into three tiers:
 *
 *  1. Surfaced  — strictly validated, rendered as form fields (the panel).
 *  2. Hidden    — live v1/infra/demo keys, loosely validated + passed through
 *                 on write, never flagged as unknown, not rendered.
 *  3. Unknown   — anything else in the file → counted in staleness.unknownKeys.
 */

/**
 * Lifecycle of one AprilTag workstation entry:
 *
 *  - `not_active`  — configured, but not the map episodes score against.
 *  - `processing`  — its scans are uploading / being fitted in the cloud.
 *  - `active`      — the one map episodes score against.
 *
 * A seed is created `not_active`, moves to `processing` when its map upload
 * starts, and reaches `active` only once a fitted map comes back — so an
 * unrefined seed can never be the scoring map. A failed or timed-out fit
 * returns it to `not_active`; there is no fourth "failed" state (the map
 * banner carries the error).
 */
export const WORKSTATION_STATUSES = [
  'not_active',
  'processing',
  'active',
] as const;

export type WorkstationStatus = (typeof WORKSTATION_STATUSES)[number];

/**
 * Reads an entry's lifecycle status, status-first: an explicit, recognized
 * `status` wins, and an absent one falls back to the legacy `active` bool.
 * Every reader must go through here — entries written before the lifecycle
 * existed (and any partial write) carry only the bool.
 */
export function workstationStatus(workstation: {
  status?: unknown;
  active?: unknown;
}): WorkstationStatus {
  const { status } = workstation;

  if (
    typeof status === 'string' &&
    (WORKSTATION_STATUSES as readonly string[]).includes(status)
  ) {
    return status as WorkstationStatus;
  }

  return workstation.active === true ? 'active' : 'not_active';
}

/** Convenience for the common "is this the scoring map?" question. */
export function isWorkstationActive(workstation: {
  status?: unknown;
  active?: unknown;
}): boolean {
  return workstationStatus(workstation) === 'active';
}

/** Tier 1: surfaced, strictly-validated keys (rendered as form fields). */
export const EchoBotConfigSurfacedSchema = zod.object({
  endpoint: zod.string().url(),
  upload: zod.boolean(),
  cloud_processing: zod.boolean(),
  upload_max_concurrent_files: zod.number().int().positive(),
  upload_max_bandwidth_mbps: zod.number().positive(),
  data_processor_job: zod.object({
    image_url: zod.string().min(1),
    service_account_key_bucket: zod.string(),
    service_account_key_file: zod.string(),
  }),
  handheld_imports: zod.object({
    // project_id/project_name scope the task & user dropdowns in the upload
    // wizard.
    project_id: zod.string().optional(),
    project_name: zod.string().optional(),
    // The single task/user every handheld .insv import is attributed to
    // (chosen in the upload wizard, which saves them here before the upload
    // starts). echo-bot uses these directly, independent of the camera model.
    task_id: zod.string().min(1),
    user_id: zod.string().optional(),
    // When true, upload .insv recordings even if api-srv reports the same raw
    // recording was already uploaded (bypasses duplicate rejection). Optional:
    // absent means "don't force" (the wizard reads it as `?? false`), so a
    // partial patch needn't carry it.
    force_upload: zod.boolean().optional(),
  }),
  apriltag_priors: zod
    .object({
      workstations: zod.array(
        zod
          .object({
            id: zod.string().min(1),
            // Lifecycle status (see WORKSTATION_STATUSES). Optional: an
            // entry written before the lifecycle existed has only the bool
            // below, which every reader falls back to. `active` is kept in
            // lockstep on write (`active === (status === 'active')`).
            status: zod.enum(WORKSTATION_STATUSES).optional(),
            active: zod.boolean().optional(),
            // Inline (hand-measured) tag parameters. Present together on an
            // inline entry; absent entirely on a reference entry, which
            // carries `map_session_id` below instead — see the refinement.
            // `tag_family`/`tag_size_m` are optional for the same reason
            // `tags` is: a reference entry has none of the three.
            tag_family: zod.string().optional(),
            tag_size_m: zod.number().positive().optional(),
            // Which tag id is the gauge-fixed anchor — omitted together with
            // tag_family/tag_size_m/tags on a reference entry, for the same
            // reason those are.
            anchor_id: zod.number().int().optional(),
            tags: zod
              .array(
                zod
                  .object({
                    id: zod.number().int(),
                    // Relative to the anchor tag's own frame (not robot
                    // home). Optional: the anchor tag's own entry (id
                    // matching the workstation's anchor_id) omits it.
                    position_m: zod
                      .tuple([zod.number(), zod.number(), zod.number()])
                      .optional(),
                    rotation_deg: zod
                      .object({
                        roll: zod.number(),
                        pitch: zod.number(),
                        yaw: zod.number(),
                      })
                      .optional(),
                    sigmas: zod
                      .object({
                        position_mm: zod.number().optional(),
                        rotation_deg: zod.number().optional(),
                      })
                      .optional(),
                  })
                  // Protects fields a future schema adds without a code
                  // change here — e.g. the frozen map's provenance stamp
                  // (station_id, measurement_date, operator,
                  // measurement_method, map_sha), which is deliberately not
                  // modelled but must survive a save.
                  .passthrough(),
              )
              .optional(),
            // How satellite tags get their final poses — see
            // `workstationYamlSchema.ts`'s `FitMethod` for the full
            // contract. Absent means `tags_only`, the pipeline default
            // (#21535).
            fit_mode: zod.enum(['tags_only', 'vio_hybrid']).optional(),
            // When true, this seed is published straight to the live map
            // with no cloud refinement pass — every satellite needs a real
            // measurement regardless of fit_mode. See the superRefine below.
            skip_refinement: zod.boolean().optional(),
            // Workstation-level uncertainty for the whole tag map, set when
            // the map is fit rather than hand-measured.
            sigmas: zod
              .object({
                position_mm: zod.number().optional(),
                rotation_deg: zod.number().optional(),
                plane_mm: zod.number().optional(),
                gravity_deg: zod.number().optional(),
              })
              .optional(),
            // Freeze-quality metrics stamped by the cloud fit
            // (skills-data-processor-job's
            // slam.map_fit.gate.build_frozen_workstation_entry), forwarded onto
            // the reference entry by echo-bot's map_fit_poller. Display-only.
            //
            // THREE distinct states, and they must not be collapsed:
            //   present + method 'fitted'            — measured agreement
            //   present + method 'seed_passthrough'  — asserted, nothing measured
            //   ABSENT                               — fitted before this block
            //                                          existed; quality UNKNOWN
            // Absent is not healthy. Never default a missing number to 0.
            freeze_quality: zod
              .object({
                schema_version: zod.number().int().optional(),
                method: zod.string().optional(),
                n_fits: zod.number().int().optional(),
                n_held_out: zod.number().int().optional(),
                fit_mode: zod.string().optional(),
                dropped_tag_id: zod.number().int().nullable().optional(),
                gate_passed: zod.boolean().optional(),
                gate_reason: zod.string().optional(),
                note: zod.string().optional(),
                // The three headline metrics, each with the offender it is
                // limited by. Optional like everything else here: a map frozen
                // before they were stamped carries the block without them, and
                // absent must render as "not recorded" — never as a zero,
                // which for every one of these is the worst possible reading.
                repeatability_mm: zod.number().nullable().optional(),
                repeatability_limited_by: zod.string().nullable().optional(),
                reprojection_p95_px: zod.number().nullable().optional(),
                reprojection_worst_fit: zod.string().nullable().optional(),
                tag_covis_seconds: zod.number().nullable().optional(),
                tag_covis_weakest_pair: zod.string().nullable().optional(),
                agreement: zod
                  .object({
                    worst_dist_spread_mm: zod.number(),
                    worst_dist_pair: zod.string(),
                    worst_rot_spread_deg: zod.number(),
                    worst_rot_tag: zod.number().int(),
                  })
                  .passthrough()
                  .nullable()
                  .optional(),
                thresholds: zod
                  .object({
                    max_dist_spread_mm: zod.number().optional(),
                    max_rot_spread_deg: zod.number().optional(),
                    usable_fit_median_px: zod.number().optional(),
                    tags_only_max_p95_px: zod.number().optional(),
                    tags_only_gross_failure_median_px: zod.number().optional(),
                  })
                  .passthrough()
                  .optional(),
                fits: zod
                  .array(
                    zod
                      .object({
                        label: zod.string(),
                        fit_mode: zod.string().optional(),
                        median_px: zod.number().nullable().optional(),
                        p95_px: zod.number().nullable().optional(),
                        rmse_px: zod.number().nullable().optional(),
                        n_corners: zod.number().int().optional(),
                        n_observations: zod.number().int().optional(),
                        solver_nfev: zod.number().int().optional(),
                        solver_status: zod.number().int().optional(),
                        observed_tag_ids: zod
                          .array(zod.number().int())
                          .nullable()
                          .optional(),
                      })
                      .passthrough(),
                  )
                  .optional(),
                held_out: zod
                  .array(
                    zod
                      .object({
                        label: zod.string(),
                        reason: zod.string().optional(),
                        n_shared_tags: zod.number().int().optional(),
                        worst_dist_diff_mm: zod.number().optional(),
                        worst_dist_pair: zod.string().optional(),
                        worst_rot_diff_deg: zod.number().optional(),
                        worst_rot_tag: zod.number().int().optional(),
                        disagrees: zod.boolean().optional(),
                      })
                      .passthrough(),
                  )
                  .optional(),
              })
              .passthrough()
              .optional(),
            // Reference to a cloud-fit `fiducials_map` artifact (D2, C-Q6) instead
            // of inline tag parameters — see the refinement below. `refined`
            // and `map_group_id` are known reference-entry keys too but are
            // deliberately left unmodelled: they already survive via
            // `.passthrough()` and don't need strict typing for the panel.
            // (`freeze_quality`, just above, IS modelled — see its own comment.)
            map_session_id: zod.string().min(1).optional(),
            map_artifact_type: zod.string().min(1).optional(),
            map_filename: zod.string().min(1).optional(),
            map_sha: zod.string().min(1).optional(),
          })
          .passthrough()
          .refine((w) => Boolean(w.tags) !== Boolean(w.map_session_id), {
            message:
              'A workstation entry must have either inline tags or a map_session_id reference, not both or neither.',
          })
          // `position_m` is optional above only so the anchor tag can omit it,
          // so the anchor/non-anchor split is what makes it required again —
          // mirroring `workstationYamlSchema.ts`'s superRefine, which gates
          // the wizard's textarea. Without this, a non-wizard write path (a
          // direct feathers-bot patch) could persist a workstation whose
          // anchor carries a pose, or whose satellites carry none.
          //
          // Scoped to entries that declare an `anchor_id`: reference entries
          // have no tags, and pre-anchor configs (still on the legacy
          // `position_home_relative_m` key — see
          // `python/apps/echo-bot/src/config/echo-bot-config-2306.yaml`)
          // have neither, so demanding `position_m` of them would leave those
          // robots unable to save any panel edit at all.
          .superRefine((w, ctx) => {
            if (w.tags === undefined || w.anchor_id === undefined) {
              return;
            }

            const anchorTags = w.tags.filter((tag) => tag.id === w.anchor_id);

            if (anchorTags.length !== 1) {
              ctx.addIssue({
                code: zod.ZodIssueCode.custom,
                path: ['anchor_id'],
                message: `anchor_id ${w.anchor_id} must match exactly one tag id, matched ${anchorTags.length}`,
              });

              return;
            }

            w.tags.forEach((tag, index) => {
              if (tag.id === w.anchor_id) {
                if (tag.position_m !== undefined) {
                  ctx.addIssue({
                    code: zod.ZodIssueCode.custom,
                    path: ['tags', index, 'position_m'],
                    message:
                      'the anchor tag (id matches anchor_id) must not carry position_m — ' +
                      'it is gauge-fixed at the origin by definition, not authored',
                  });
                }

                if (tag.rotation_deg !== undefined) {
                  ctx.addIssue({
                    code: zod.ZodIssueCode.custom,
                    path: ['tags', index, 'rotation_deg'],
                    message:
                      'the anchor tag (id matches anchor_id) must not carry rotation_deg — ' +
                      'it is gauge-fixed at identity rotation by definition, not authored',
                  });
                }
              } else if (tag.position_m === undefined) {
                // Same fit_mode/skip_refinement truth table as
                // `workstationYamlSchema.ts`'s superRefine — duplicated
                // rather than shared because the two schemas live in
                // separate packages (remote-control app vs. this lib).
                // Keep both in sync if this changes.
                if (w.skip_refinement === true) {
                  ctx.addIssue({
                    code: zod.ZodIssueCode.custom,
                    path: ['tags', index, 'position_m'],
                    message:
                      'skip_refinement publishes the seed as the live map — ' +
                      'every satellite tag needs a measured position_m',
                  });
                } else if ((w.fit_mode ?? 'tags_only') === 'vio_hybrid') {
                  ctx.addIssue({
                    code: zod.ZodIssueCode.custom,
                    path: ['tags', index, 'position_m'],
                    message: 'position_m is required for every non-anchor tag',
                  });
                }
              }
            });
          }),
      ),
    })
    .passthrough(),
  handheld_camera: zod.object({ t_flange_cam_yaml: zod.string() }),
});

/**
 * Tier 2: known-but-hidden keys. Live (v1 recording / infra / demo) but not
 * surfaced. Loosely typed, never flagged as unknown, passed through on write.
 */
export const ECHO_BOT_HIDDEN_KEYS = [
  'host',
  'port',
  'gripper_joint_numbers',
  'gripper_zero_is_closed',
  'recording_dir',
  'bot_identity_config_path',
  'tcp_offset',
  'robot_name',
  'use_ee_board',
  'use_default_recorder_config',
  'default_config',
] as const;

/** Full config = surfaced (strict) + hidden (passthrough) + unknown (kept). */
export const EchoBotConfigSchema = EchoBotConfigSurfacedSchema.extend({
  host: zod.string().optional(),
  port: zod.number().optional(),
  gripper_joint_numbers: zod.array(zod.array(zod.number())).optional(),
  gripper_zero_is_closed: zod.boolean().optional(),
  recording_dir: zod.string().optional(),
  bot_identity_config_path: zod.string().optional(),
  tcp_offset: zod.array(zod.number()).optional(),
  robot_name: zod.string().optional(),
  use_ee_board: zod.boolean().optional(),
  use_default_recorder_config: zod.boolean().optional(),
  default_config: zod
    .object({ user_id: zod.string(), task_id: zod.string() })
    .optional(),
}).passthrough();

export type EchoBotConfig = zod.infer<typeof EchoBotConfigSchema>;

/** Keys echo-bot expects (surfaced + hidden); used for staleness. */
export const ECHO_BOT_KNOWN_KEYS: string[] = [
  ...Object.keys(EchoBotConfigSurfacedSchema.shape),
  ...ECHO_BOT_HIDDEN_KEYS,
];

/**
 * The production api-srv GraphQL endpoint. Anything else (a preview or staging
 * api-srv) routes recordings into a non-production dataset, so the UI surfaces
 * a non-blocking warning when the configured endpoint differs.
 */
export const ECHO_BOT_PROD_GRAPHQL_ENDPOINT =
  'https://api.standardbots.com/graphql';

/** Lowercased, whitespace- and trailing-slash-free form for comparison. */
function normalizeEndpoint(endpoint: string): string {
  return endpoint.trim().replace(/\/+$/, '').toLowerCase();
}

/**
 * Trailing-slash- and case-insensitive comparison against
 * `ECHO_BOT_PROD_GRAPHQL_ENDPOINT`. An absent or empty endpoint is not prod —
 * callers that can't read the config at all must skip the check rather than
 * pass undefined, so a failed fetch never shows a false warning.
 */
export function isProdEchoBotEndpoint(
  endpoint: string | null | undefined,
): boolean {
  if (!endpoint) {
    return false;
  }

  return (
    normalizeEndpoint(endpoint) ===
    normalizeEndpoint(ECHO_BOT_PROD_GRAPHQL_ENDPOINT)
  );
}

export interface EchoBotConfigState {
  /** Did the `/etc` override file exist, or are we showing bundled defaults? */
  source: 'override-file' | 'defaults';
  staleness: { missingKeys: string[]; unknownKeys: string[] };
  /** snake_case, mirrors the yaml under `default:`. */
  config: EchoBotConfig;
}

/**
 * Pure helper — shared by the feathers service and tests.
 *
 * `missingKeys` = known keys (tiers 1+2) absent from the file → e.g. a stale
 * file missing `handheld_imports`. `unknownKeys` = keys in the file that aren't
 * known → dead cruft like `vicon_host`.
 */
export function computeStaleness(fileKeys: string[]): {
  missingKeys: string[];
  unknownKeys: string[];
} {
  const known = new Set(ECHO_BOT_KNOWN_KEYS);
  const present = new Set(fileKeys);

  return {
    missingKeys: ECHO_BOT_KNOWN_KEYS.filter((k) => !present.has(k)),
    unknownKeys: fileKeys.filter((k) => !known.has(k)),
  };
}
